import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Pensée du jour — carte compacte pour la page d'accueil.
 *
 * Ne reprend pas la page dédiée : le lecteur doit pouvoir lire la pensée en
 * une seconde, puis décider s'il veut la suite.
 *
 * `quote` peut être passée par la home si la donnée y est déjà : dans ce cas
 * aucun appel réseau supplémentaire n'est fait.
 *
 * La hauteur est réservée dès le premier rendu, y compris pendant le
 * chargement — une carte qui apparaît en poussant le contenu sous elle est
 * exactement ce qui fait rater un clic sur mobile.
 */
const PenseeDuJourWidget = ({ quote: provided = null }) => {
  const [quote, setQuote] = useState(provided);
  const [state, setState] = useState(provided ? 'ready' : 'loading');

  useEffect(() => {
    if (provided) { setQuote(provided); setState('ready'); return; }
    let alive = true;
    axios.get(`${API_URL}/api/quotes/today`)
      .then((r) => { if (alive) { setQuote(r.data); setState('ready'); } })
      .catch(() => { if (alive) setState('empty'); });
    return () => { alive = false; };
  }, [provided]);

  // Rien à montrer : on disparaît proprement plutôt que d'afficher une carte
  // vide qui ressemblerait à une panne.
  if (state === 'empty') return null;

  const label = (
    <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#D62828]">
      Pensée du jour
    </p>
  );

  if (state === 'loading') {
    return (
      <section aria-busy="true" data-testid="pensee-widget-loading"
               className="rounded-xl border border-border bg-card p-4 sm:p-5 min-h-[132px]">
        {label}
        <div className="mt-3 flex gap-4">
          <div className="h-16 w-16 shrink-0 animate-pulse rounded-lg bg-muted" />
          <div className="flex-1 space-y-2">
            <div className="h-3 w-28 animate-pulse rounded bg-muted" />
            <div className="h-4 w-full animate-pulse rounded bg-muted" />
            <div className="h-4 w-4/5 animate-pulse rounded bg-muted" />
          </div>
        </div>
      </section>
    );
  }

  const initiales = (quote.author || '?')
    .split(/\s+/).slice(0, 2).map((m) => m[0]).join('').toUpperCase();

  return (
    <Link to="/pensee-du-jour" data-testid="pensee-widget"
          aria-label={`Pensée du jour de ${quote.author}`}
          className="group block rounded-xl border border-border bg-card p-4 sm:p-5
                     transition-colors hover:border-[#D62828]/60 focus:outline-none
                     focus-visible:ring-2 focus-visible:ring-[#D62828] min-h-[132px]">
      {label}

      <div className="mt-3 flex gap-4">
        {/* Portrait quand les droits sont établis ; sinon les initiales de
            l'auteur — jamais un visage fabriqué. */}
        {quote.visual_kind === 'portrait' && quote.portrait_url ? (
          <img src={quote.portrait_url} alt={quote.author} loading="lazy"
               width={64} height={64}
               className="h-16 w-16 shrink-0 rounded-lg object-cover grayscale" />
        ) : (
          <div aria-hidden="true"
               className="flex h-16 w-16 shrink-0 items-center justify-center
                          rounded-lg bg-neutral-900 text-lg font-bold text-white">
            {initiales}
          </div>
        )}

        <div className="min-w-0 flex-1">
          <p className="truncate text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {quote.author}
          </p>
          <blockquote className="mt-1 line-clamp-4 text-[15px] font-semibold leading-snug
                                 text-foreground sm:text-base">
            «&nbsp;{quote.text}&nbsp;»
          </blockquote>
        </div>
      </div>

      <span className="mt-3 inline-flex items-center gap-1 text-xs font-medium
                       text-[#D62828] group-hover:gap-2 transition-all">
        Découvrir toutes les pensées
        <ArrowRight className="h-3.5 w-3.5" />
      </span>
    </Link>
  );
};

export default PenseeDuJourWidget;
